import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminRole } from '@/lib/api-admin';
import { adminFetch } from '@/lib/apiClient';
import type { ApiMeta } from '@/lib/http-core';
import {
  downloadViaLink,
  exportJobsPollInterval,
  toExportJobError,
  type ExportJobError,
} from '@/lib/exportJobs';

/**
 * 상품 엑셀 다운로드 요청(catalog-service AdminProductExportJobController, release-qa/v1.0.18).
 * 주문 엑셀(/api/admin/v1/orders/exports)과 상태값·받기 방식이 같다. 상품 엑셀은 행 수 상한(일반 5,000 /
 * 전체필드 2,000)이 있어 파일은 항상 1개다.
 */
const BASE = '/admin/v1/products/exports';

/** BE @PreAuthorize 와 같은 권한(catalog AdminProductExportJobController — MD·OPS·SUPER·AUDITOR). QA-514: 운영 관리자 빠져 있던 것 추가. */
export const PRODUCT_EXPORT_JOB_ROLES: ReadonlyArray<AdminRole> = ['MD_MANAGER', 'OPS_ADMIN', 'SUPER_ADMIN', 'AUDITOR'];
export const PRODUCT_EXPORT_JOBS_PATH = '/products/exports';

/** PRODUCT = 상품 엑셀, PRODUCT_FULL = 전체필드(등록 양식) 엑셀. */
export type ProductExportType = 'PRODUCT' | 'PRODUCT_FULL';

export interface ProductExportJob {
  exportJobId: string;
  exportType: ProductExportType | string;
  status: string;
  fileName: string | null;
  fileSizeBytes: number | null;
  errorMessage: string | null;
  requestedBy: string | null;
  requestedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
}

export type ProductExportCriteria = Record<string, string | number | boolean>;

const MESSAGE_PASSTHROUGH_CODES: ReadonlySet<string> = new Set([
  'COMMON_VALIDATION_FAILED',
  'CATALOG_EXPORT_JOB_NOT_FOUND',
  'CATALOG_EXPORT_JOB_NOT_READY',
]);
export const toProductExportError = (res: Response): Promise<ExportJobError> =>
  toExportJobError(res, MESSAGE_PASSTHROUGH_CODES);

const NUMBER_KEYS = new Set(['priceMin', 'priceMax', 'stockMin', 'stockMax']);
const BOOLEAN_KEYS = new Set(['hasArtist', 'displayed', 'hasOption']);

/**
 * 즉시 엑셀 쿼리(buildProductExportQuery / buildProductExportFullQuery) → criteria 객체.
 * BE ProductExportCriteria 타입에 맞춰 숫자·불리언은 그 타입으로, 나머지(등록일 ISO 시각·fields CSV 등)는 문자열.
 */
export const criteriaFromProductQuery = (query: string): ProductExportCriteria => {
  const out: ProductExportCriteria = {};
  for (const [key, value] of new URLSearchParams(query)) {
    if (NUMBER_KEYS.has(key)) out[key] = Number(value);
    else if (BOOLEAN_KEYS.has(key)) out[key] = value === 'true';
    else out[key] = value;
  }
  return out;
};

export const productExportJobsKey = ['admin', 'product-export-jobs'] as const;

export interface ProductExportJobsPage {
  items: ProductExportJob[];
  meta: ApiMeta | null;
}

/** 내 요청 목록(최신순, SUPER_ADMIN 은 전체). 대기·생성 중이 있을 때만 다시 불러온다. */
export const useProductExportJobsQuery = (page: number, size: number) =>
  useQuery<ProductExportJobsPage, ExportJobError>({
    queryKey: [...productExportJobsKey, page, size],
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?page=${Math.max(0, page - 1)}&size=${size}`);
      if (!res.ok) throw await toProductExportError(res);
      const env = (await res.json()) as { data: ProductExportJob[] | null; meta?: ApiMeta | null };
      return { items: env.data ?? [], meta: env.meta ?? null };
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) => exportJobsPollInterval(query.state.data?.items),
  });

/** 다운로드 요청(202). 같은 Idempotency-Key 로 다시 보내면 같은 요청을 돌려준다(두 번 눌러도 1건). */
export const useRequestProductExportMutation = () => {
  const qc = useQueryClient();
  return useMutation<
    ProductExportJob,
    ExportJobError,
    { type: ProductExportType; criteria: ProductExportCriteria; idempotencyKey: string }
  >({
    mutationFn: async ({ type, criteria, idempotencyKey }) => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ type, criteria }),
      });
      if (!res.ok) throw await toProductExportError(res);
      return ((await res.json()) as { data: ProductExportJob }).data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: productExportJobsKey }),
  });
};

export const useDeleteProductExportJobMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, ExportJobError, string>({
    mutationFn: async (exportJobId) => {
      const res = await adminFetch(`${BASE}/${encodeURIComponent(exportJobId)}`, { method: 'DELETE' });
      if (!res.ok) throw await toProductExportError(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: productExportJobsKey }),
  });
};

/** 파일 받기 — 링크를 새로 받아 S3 직접(QA·운영) 또는 blob(로컬). */
export const downloadProductExportFile = (exportJobId: string, fileName: string): Promise<void> =>
  downloadViaLink(`${BASE}/${encodeURIComponent(exportJobId)}/download-url`, fileName, toProductExportError);
